import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo } from 'react';
import { styled } from 'baseui';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import _isUndefined from 'lodash/isUndefined';
import { LABELLING_TYPE } from '../../../../';
import { useAPIProvider } from '../../../../common/hooks/use-api-provider';
import type { GetULabelConfigFunction } from '../../../../common/providers/feature-flag-provider';
import { isFocusedModeEnabledAtom } from '../../../../common/review-mode-side-panel/atoms';
import { TASK_MODE } from '../../../../common/task-state/constants';
import useTaskStateStore from '../../../../common/task-state/providers/hooks/useTaskStateStore';
import { getGroupingBoxDimensions } from '../../../labelling/components/annotations/utils';
import Editor from '../../../labelling/components/editor/editor';
import { VideoViewSelector } from '../../../labelling/components/editor/video-view-selector';
import useAnalyticsForVideoLabelling from '../../../labelling/components/frames-control-panel/analytics/useAnalyticsForVideoLabelling';
import FramesControlPanel from '../../../labelling/components/frames-control-panel/frames-control-panel';
import { ACCURATE_FRAME_RENDERING_LOADING } from '../../../labelling/constants';
import { TOOL } from '../../../labelling/constants';
import { MediaStateProvider } from '../../../labelling/providers/media-state-provider';
import { MediaStateProviderLite } from '../../../labelling/providers/media-state-provider-lite';
import { StageProvider } from '../../../labelling/providers/stage-provider';
import type { SuperpixelImageRegionsDataT } from '../../../labelling/types';
import type { BoundingBoxTrackingInputT, StudioRef } from '../../../types';
import { annotationUnderUserReviewAtom, canvasAnnotationStoreAtom, consensusAnnotationFramesAtom, currentFrameImageUrlAtom, enhancedMLAnnotationFramesWithUIStateAtom, frameCountAtom } from '../../atoms/annotation-atoms';
import { dataUrlAtom, fileTypeAtom, mediaFileRefAtom, metadataAtom } from '../../atoms/file-atoms';
import { isGridViewEnabledAtom } from '../../atoms/grid-view-atoms';
import { imageSettingsAtom } from '../../atoms/image-settings-atoms';
import { dispatchAtom } from '../../atoms/label-store-dispatch/dispatch';
import { EVENTS } from '../../atoms/label-store-dispatch/events';
import { multiVideoListAtom, selectedVideoIndexAtom } from '../../atoms/multi-video-atoms';
import { relationshipEditorOpenAtom, visibleRelationshipsAtom } from '../../atoms/relationship-atoms';
import { visibilityModeAtom } from '../../atoms/side-panel-atoms';
import { editorModeAtom } from '../../atoms/stage-atoms';
import { taskModeAtom } from '../../atoms/task-mode-atoms';
import { brushToolSettingsAtom, copiedAnnotationIdAtom, focusedAnnotationIdAtom, gridModeAtom, isLoadingAtom, multiSelectEnabledAtom, selectedAnnotationIdsAtom, selectedAnnotationLabelAtom, selectedSubToolAtom, selectedToolAtom, snapEnabledAtom } from '../../atoms/tool-atoms';
import { accurateFrameRenderingAtom } from '../../atoms/video-playback-atoms';
import { currentFrameAtom, hoveredFrameAtom } from '../../atoms/video-playback-atoms';
import { useFitToScreen } from '../../hooks/use-fit-to-screen';
import { useMlTrackingJobRunner } from '../../hooks/use-ml-tracking-job-runner';
import { useStageDispatchAtom } from '../../hooks/use-stage-dispatch-atom';
import { useStageSettings } from '../../hooks/use-stage-settings';
import { StageOverlayElements } from '../stage-overlay-elements';
import { StageScope } from '../stage-scope';
import { VideoGrid } from '../video-grid/video-grid';
import { VideoGrid as VideoGridV2 } from '../video-grid/video-grid-v2';
const Container = styled('div', () => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflow: 'hidden'
}));
export const FramePreviewContainer = styled('div', () => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflow: 'hidden'
}));
export const Container1 = styled('div', () => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignSelf: 'start',
  width: '100%'
}));
type PropsT = {
  showPredefinedSAMToolSizes?: boolean;
  enableUndoRedoStageToolBar?: boolean;
  enableStageToolBar?: boolean;
  getSuperpixelImageRegionsData?: () => Promise<SuperpixelImageRegionsDataT>;
  frameRate?: number;
  getULabelConfig?: GetULabelConfigFunction;
};
export type EditorContainerRef = Pick<StudioRef, 'selectAnnotation'>;
const EditorContainer = forwardRef<EditorContainerRef, PropsT>(({
  getULabelConfig,
  showPredefinedSAMToolSizes,
  frameRate
}, ref) => {
  const {
    readOnly
  } = useTaskStateStore();
  const {
    getAnnotationTrackingData,
    getBoundingBoxData,
    trackObjectsAcrossKeyframes
  } = useAPIProvider();
  const dispatch = useSetAtom(dispatchAtom);
  useMlTrackingJobRunner({
    trackObjects: trackObjectsAcrossKeyframes
  });
  const stageDispatchAtom = useStageDispatchAtom();
  const annotationStore = useAtomValue(canvasAnnotationStoreAtom);
  const frameCount = useAtomValue(frameCountAtom);
  const currentFrame = useAtomValue(currentFrameAtom);
  const selectedAnnotationIds = useAtomValue(selectedAnnotationIdsAtom);
  const selectedAnnotationLabel = useAtomValue(selectedAnnotationLabelAtom);
  const selectedTool = useAtomValue(selectedToolAtom);
  const focusedAnnotationId = useAtomValue(focusedAnnotationIdAtom);
  const copiedAnnotationId = useAtomValue(copiedAnnotationIdAtom);
  const metaData = useAtomValue(metadataAtom);
  const dataUrl = useAtomValue(dataUrlAtom);
  const [taskMode] = useAtom(taskModeAtom);
  const isReviewMode = taskMode === TASK_MODE.REVIEW;
  const isVideoGridEnabled = useAtomValue(isGridViewEnabledAtom);
  const fileType = useAtomValue(fileTypeAtom);
  const multiVideoList = useAtomValue(multiVideoListAtom);
  const [selectedVideoIndex, setSelectedVideoIndex] = useAtom(selectedVideoIndexAtom);
  const setMediaFileRef = useSetAtom(mediaFileRefAtom);
  const isMultiVideo = multiVideoList.length > 1;
  const numberOfVideos = multiVideoList.length ?? 0;
  const {
    sendEventVideoLoad
  } = useAnalyticsForVideoLabelling();
  useEffect(() => {
    if (fileType !== 'VIDEO') {
      return;
    }
    sendEventVideoLoad({
      isMultiVideo,
      numberOfVideos
    });
  }, [sendEventVideoLoad, isMultiVideo, numberOfVideos, fileType]);
  const handleVideoSelection = useCallback((index: number) => {
    if (multiVideoList[index]) {
      const selectedVideo = multiVideoList[index];
      dispatch({
        type: EVENTS.SELECT_ANNOTATION_LABEL,
        annotationLabel: null
      });
      dispatch({
        type: EVENTS.SELECT_TOOL,
        tool: TOOL.POINTER,
        subTool: undefined
      });
      setMediaFileRef(selectedVideo.referenceUri);
      setSelectedVideoIndex(index);
    }
  }, [multiVideoList, dispatch, setMediaFileRef, setSelectedVideoIndex]);

  // Disable annotations when not on main view (index 0)
  const isMainView = selectedVideoIndex === 0;
  const effectiveReadOnly = readOnly || !isMainView;
  const accurateFrameRenderingFlag = useAtomValue(accurateFrameRenderingAtom);
  useImperativeHandle(ref, () => ({
    selectAnnotation: (_frameIndex: number, annotationId: string) => {
      dispatch({
        type: EVENTS.SELECT_ANNOTATION,
        annotationId
      });
    }
  }));
  const handleFrameHover = useCallback((frameId: number | null) => {
    dispatch({
      type: EVENTS.SET_HOVERED_FRAME,
      frameId
    });
  }, [dispatch]);
  const getBoundingBoxTrackingData = useMemo(() => {
    if (!getBoundingBoxData) return undefined;
    return (frameIndex: number, bbox: BoundingBoxTrackingInputT['bbox'], numFramesToTrack: number) => {
      return getBoundingBoxData({
        videoUrl: dataUrl ?? '',
        initialFrame: frameIndex,
        bbox,
        numFramesToTrack
      });
    };
  }, [getBoundingBoxData, dataUrl]);
  const shouldRenderFrameControlPanel = fileType === LABELLING_TYPE.PDF || frameCount > 1;
  const isVideoPlayer = !_isUndefined(frameRate);
  const [stageSettings, setStageSettings] = useStageSettings();
  const [_, setFitToScreen] = useFitToScreen();
  if (isVideoPlayer && accurateFrameRenderingFlag === ACCURATE_FRAME_RENDERING_LOADING) return null;
  const isAccurateFrameRenderingEnabled = isMainView && accurateFrameRenderingFlag === true;
  const videoGrid = isAccurateFrameRenderingEnabled ? <VideoGridV2 /> : <VideoGrid />;
  const onMount = (fn: () => void) => {
    setFitToScreen(fn);
  };
  const stage = <StageScope config={{
    annotationStore: canvasAnnotationStoreAtom,
    mlAnnotationFrames: enhancedMLAnnotationFramesWithUIStateAtom,
    consensusAnnotationFrames: consensusAnnotationFramesAtom,
    imageUrl: currentFrameImageUrlAtom,
    frameCount: frameCountAtom,
    currentFrame: currentFrameAtom,
    annotationUnderUserReview: annotationUnderUserReviewAtom,
    selectedAnnotationIds: selectedAnnotationIdsAtom,
    selectedAnnotationLabel: selectedAnnotationLabelAtom,
    selectedTool: selectedToolAtom,
    selectedSubTool: selectedSubToolAtom,
    multiSelectEnabled: multiSelectEnabledAtom,
    gridMode: gridModeAtom,
    focusedAnnotationId: focusedAnnotationIdAtom,
    brushToolSettings: brushToolSettingsAtom,
    hoveredFrame: hoveredFrameAtom,
    snapEnabled: snapEnabledAtom,
    editorMode: editorModeAtom,
    isLoading: isLoadingAtom,
    copiedAnnotationId: copiedAnnotationIdAtom,
    fileType: fileTypeAtom,
    metadata: metadataAtom,
    dataUrl: dataUrlAtom,
    relationshipEditorOpen: relationshipEditorOpenAtom,
    dispatchAtom: stageDispatchAtom,
    imageSettings: imageSettingsAtom,
    taskMode: taskModeAtom,
    isFocusModeEnabled: isFocusedModeEnabledAtom,
    visibleRelationships: visibleRelationshipsAtom,
    visibilityMode: visibilityModeAtom
  }}>
        <StageProvider showPredefinedSAMToolSizes={showPredefinedSAMToolSizes} onMount={onMount} stageSettings={stageSettings} setLocalStageSettings={setStageSettings} frameState={{
      annotationStore,
      currentFrame,
      frameCount
    }} toolState={{
      selectedAnnotationIds,
      selectedAnnotationLabel: selectedAnnotationLabel ?? undefined,
      selectedTool,
      focusedAnnotationId: focusedAnnotationId ?? undefined,
      copiedAnnotationId
    }} file={{
      type: fileType,
      metadata: metaData,
      dataUrl: dataUrl ?? undefined
    }} getGroupingBoxDimensions={getGroupingBoxDimensions} readOnly={effectiveReadOnly} isReviewMode={isReviewMode} renderOverlay={(container?: HTMLDivElement) => <StageOverlayElements container={container ?? null} getULabelConfig={getULabelConfig ?? null} />}>
          <Editor readOnly={effectiveReadOnly} getULabelConfig={getULabelConfig} isVideoPlayer={!!isVideoPlayer} />
        </StageProvider>
      </StageScope>;
  return <MediaStateProviderLite frameRate={frameRate}>
        <MediaStateProvider frameRate={frameRate} isVideoPlayer={isVideoPlayer} isAccurateFrameRenderingEnabled={isAccurateFrameRenderingEnabled}>
          <Container>
            {isVideoPlayer && multiVideoList.length > 1 && <VideoViewSelector onSelectVideo={handleVideoSelection} />}
            {isVideoGridEnabled ? videoGrid : stage}
            {shouldRenderFrameControlPanel ? <FramesControlPanel onFrameHover={handleFrameHover} isVideoPlayer={isVideoPlayer} fetchAnnotationTrackingData={getAnnotationTrackingData} {...getBoundingBoxData ? {
          getBoundingBoxTrackingData
        } : {}} /> : null}
          </Container>
        </MediaStateProvider>
      </MediaStateProviderLite>;
});
EditorContainer.displayName = 'EditorContainer';
export default EditorContainer;